import { useState } from 'react';
import { Pressable, View } from 'react-native';
import { format } from 'date-fns';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Text } from '@/components/ui/text';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/date-picker/DatePicker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import type { BaseModalProps } from '@/contexts/ModalContext';
import { usePowerSync } from '~/powersync/contexts/power-sync/PowerSync';
import { db } from '@/lib/local-api';
import { useHistoryMinDate } from '~/calendar/hooks/useHistoryMinDate';
import { Icon, type IconProps } from '~/global/components/icon/Icon';
import {
  KEEP_ORIGINAL,
  type MealLogCopyDestination,
  type MealLogCopyMode,
  type MealLogCopyResult,
} from '@/lib/local-api/meal/meal-log-copy';
import type { MealSlot } from '@/lib/local-api/meal/meal-slot-grouper';

/** The slot chooser's own "leave each item where it is" option. */
const KEEP = 'KEEP' as const;
type SlotChoice = typeof KEEP | MealSlot;

const SLOT_ICONS: Record<MealSlot, IconProps['name']> = {
  BREAKFAST: 'Sunrise',
  LUNCH: 'Sun',
  SNACK: 'Apple',
  DINNER: 'Moon',
};

/** Display order, matching `MealTypeSelector`. */
const SLOT_ORDER: MealSlot[] = ['BREAKFAST', 'LUNCH', 'SNACK', 'DINNER'];

export interface MealLogCopyModalProps extends BaseModalProps {
  /**
   * The logs to operate on, **as ids**. Deliberately not records: every calling
   * screen's row type is narrower than `MealLogRecord`, and `copyLogs` re-reads
   * the full rows inside its transaction anyway (DD10).
   */
  sourceIds: string[];
  mode: MealLogCopyMode;
  /** The source day — the picker's default, which writes `consumedAt` back verbatim (DD2). */
  defaultDate: Date;
  onDone: (result: MealLogCopyResult) => void;
  portalHost?: string;
}

/**
 * Asks where a set of logged meals should land, then performs the copy/move
 * locally and hands the result on (the summary modal is opened by the caller —
 * see `useMealLogCopy`).
 *
 * **There is deliberately no "keep the original day" toggle.** The picker
 * defaults to the source day, and under DD2's placement rule that writes each
 * source's `consumedAt` back byte-for-byte — so a keep-day control would be a
 * second way to express what doing nothing already does. The slot chooser still
 * needs its explicit "Keep original", because a selection can span slots and so
 * has no single source slot to default to.
 *
 * A destination identical to the source is **permitted** (the second-helping
 * case); confirm is disabled only on an empty selection.
 */
export const MealLogCopyModal = ({
  onClose,
  sourceIds,
  mode,
  defaultDate,
  onDone,
  portalHost,
}: MealLogCopyModalProps) => {
  const { t } = useTranslation('meal');
  const powerSync = usePowerSync();
  const minimumDate = useHistoryMinDate();
  const [date, setDate] = useState<Date>(defaultDate);
  const [slot, setSlot] = useState<SlotChoice>(KEEP);

  const count = sourceIds.length;

  const copyMutation = useMutation({
    mutationFn: () => {
      const destination: MealLogCopyDestination = {
        day: format(date, 'yyyy-MM-dd'),
        mealType: slot === KEEP ? KEEP_ORIGINAL : slot,
      };
      return db.MealLog.mutations.copyLogs(
        powerSync,
        sourceIds,
        destination,
        mode,
      );
    },
    onSuccess: (result) => {
      onClose();
      onDone(result);
    },
  });

  const title = t('copy.modal.title', { context: mode, count });

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent portalHost={portalHost}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{t('copy.modal.description')}</DialogDescription>
        </DialogHeader>

        <View className="gap-2">
          <Text className="text-xs font-inter-semibold uppercase text-text-muted">
            {t('copy.modal.dayLabel')}
          </Text>
          <DatePicker
            testID="meal-copy-date"
            value={date}
            onChange={setDate}
            label={t('copy.modal.dateLabel')}
            minimumDate={minimumDate ?? undefined}
          />
          <Text className="text-xs text-text-muted">
            {t('copy.modal.dateCaption')}
          </Text>
        </View>

        <View className="gap-2">
          <Text className="text-xs font-inter-semibold uppercase text-text-muted">
            {t('copy.modal.slotLabel')}
          </Text>

          <Pressable
            testID="meal-copy-slot-keep"
            collapsable={false}
            onPress={() => setSlot(KEEP)}
            className={`flex-row items-center gap-2 rounded-xl px-4 py-3 ${
              slot === KEEP ? 'bg-accent-soft' : 'bg-surface-elevated'
            }`}
          >
            <Icon
              name="Pin"
              size={16}
              className={slot === KEEP ? 'text-accent' : 'text-text-muted'}
            />
            <Text
              className={`flex-1 text-sm ${
                slot === KEEP
                  ? 'font-inter-semibold text-accent'
                  : 'font-inter-medium text-text-secondary'
              }`}
            >
              {t('copy.modal.keepOriginal')}
            </Text>
            {slot === KEEP ? (
              <Icon name="Check" size={16} className="text-accent" />
            ) : null}
          </Pressable>

          <View className="flex-row gap-2">
            {SLOT_ORDER.map((option) => {
              const isActive = slot === option;
              return (
                <Pressable
                  key={option}
                  testID={`meal-copy-slot-${option.toLowerCase()}`}
                  collapsable={false}
                  onPress={() => setSlot(option)}
                  className={`h-14 flex-1 items-center justify-center gap-1 rounded-xl ${
                    isActive ? 'bg-accent-soft' : 'bg-surface-elevated'
                  }`}
                >
                  <Icon
                    name={SLOT_ICONS[option]}
                    size={18}
                    className={isActive ? 'text-accent' : 'text-text-muted'}
                  />
                  <Text
                    className={`text-xs ${
                      isActive
                        ? 'font-inter-semibold text-accent'
                        : 'font-inter-medium text-text-muted'
                    }`}
                  >
                    {t(`mealType.${option.toLowerCase()}`)}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </View>

        <DialogFooter>
          <Button
            testID="meal-copy-cancel"
            variant="subtle"
            className="flex-1"
            onPress={onClose}
            disabled={copyMutation.isPending}
          >
            <Text>{t('copy.modal.cancel')}</Text>
          </Button>
          <Button
            testID="meal-copy-confirm"
            className="flex-1"
            disabled={count === 0}
            loading={copyMutation.isPending}
            onPress={() => copyMutation.mutate()}
          >
            <Text>{t('copy.modal.confirm', { context: mode, count })}</Text>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
